import { useSelector } from 'react-redux';
import { selectors } from 'store';

import { BRAND_ORANGE } from './bundleBuilderConfig';

/**
 * The accent colour the upgrade popups should carry.
 *
 * The LMS is themed per active course — CNS runs green, CPT orange — via the course's admin-set
 * `upsellPrimaryColor`, falling back to the direction's main colour. ChooseProductModal already
 * resolves its accent exactly this way; the builder popups hardcoding brand orange is what made
 * them look pasted-in on a green course.
 *
 * Returned as CSS custom properties so the styled-components can stay static: one wrapper sets
 * `--accent` (and derived alpha tints) and every colour below it follows.
 */
export const useUpsellAccent = (): { accent: string; vars: Record<string, string> } => {
  const activeCourse = useSelector(selectors.courses.selectActiveCourse);
  const direction = useSelector(selectors.direction.selectDirection);

  const courseColor =
    typeof (activeCourse as { upsellPrimaryColor?: string } | null)?.upsellPrimaryColor === 'string'
      ? ((activeCourse as { upsellPrimaryColor?: string }).upsellPrimaryColor as string).trim()
      : '';

  const accent = courseColor || direction?.mainColorHex || BRAND_ORANGE;

  return {
    accent,
    vars: {
      '--accent': accent,
      // Hex-with-alpha tints; only meaningful for #rrggbb accents, which is what admin stores.
      '--accent-soft': `${accent}0D`,
      '--accent-border': `${accent}40`,
      '--accent-glow': `${accent}29`,
      '--accent-mid': `${accent}52`,
      '--accent-strong': `${accent}8C`,
    },
  };
};
